import { CircularProgress, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Konva from 'konva';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef } from 'react';
import { Image, Layer, Stage } from 'react-konva';
import useImage from 'use-image';
import { updateCropScale, updateCropToolProps, updateImageProps, updateStageProps } from './actions';
import { DEFAULT_CROP_TOOL_PROPS, MAX_STAGE_HEIGHT } from './constants';
import { ImageEditorContext } from './context';
import CropTool from './CropTool';
import styles from './ImageEditor.styles';
import Toolbar from './Toolbar';

const propTypes = {
  classes: PropTypes.object,
  image: PropTypes.object.isRequired,
};

function ImageEditor({ classes, image: formImage, style }) {
  const {
    state: { saving, brightness, imageCrop, stageProps, cropToolProps, imageProps, cropping, keepAspectRatio },
    dispatch,
  } = useContext(ImageEditorContext);
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const imageRef = useRef(null);
  const imageLayerRef = useRef(null);
  const cropToolRef = useRef(null);
  const [image] = useImage(formImage.imageUri, 'Anonymous');

  // Set up the resize handler and check for initial container size
  useEffect(() => {
    window.addEventListener('resize', checkContainerSize);
    checkContainerSize();
    return () => {
      window.removeEventListener('resize', checkContainerSize);
    };
  }, []);

  // If the state, image, or imageCrop changes, recalculate the image scaling
  useEffect(() => {
    if (image && imageCrop && stageProps) {
      calculateImageSize();
    }
  }, [image, imageCrop, stageProps]);

  // If the imageCrop changes reapply filters because cropping the image will clear them
  useEffect(() => {
    if (imageCrop && imageRef.current) {
      applyFilters();
    }
  }, [imageCrop]);

  // Set the stage size to the size of the containing div
  const checkContainerSize = () => {
    dispatch(
      updateStageProps({
        height:
          containerRef.current.offsetHeight > MAX_STAGE_HEIGHT ? MAX_STAGE_HEIGHT : containerRef.current.offsetHeight,
        width: containerRef.current.offsetWidth,
      }),
    );
  };

  // Check the size of the current image and update image scaling accordingly
  const calculateImageSize = () => {
    let width = imageCrop.width > 0 ? imageCrop.width : image.width;
    let height = imageCrop.height > 0 ? imageCrop.height : image.height;

    const aspectRatio = width / height;

    const stageWidthScale = stageProps.width / width;
    const stageHeightScale = stageProps.height / height;

    const maxWidth = width * stageWidthScale;
    const maxHeight = height * stageHeightScale;

    /**
     * find scaled width if we take the max height
     * 4 / 3 = maxWidth / maxHeight (example aspect ratio)
     * (4 * maxHeight) / 3 = maxWidth
     */

    const scaledWidth = aspectRatio * maxHeight;
    const scaledHeight = maxWidth / aspectRatio;

    if (scaledWidth > maxWidth) {
      // use width to scale
      width = maxWidth;
      height = scaledHeight;
    } else {
      // use height to scale
      width = scaledWidth;
      height = maxHeight;
    }

    dispatch(updateCropScale({ x: image.width / width, y: image.height / height }));
    dispatch(
      updateImageProps({
        x: width / 2,
        y: height / 2,
        height,
        width,
        offset: {
          x: width / 2,
          y: height / 2,
        },
      }),
    );

    if (
      cropToolProps.height === DEFAULT_CROP_TOOL_PROPS.height &&
      cropToolProps.width === DEFAULT_CROP_TOOL_PROPS.width
    ) {
      dispatch(
        updateCropToolProps({
          height,
          width,
        }),
      );
    }
  };

  const applyFilters = () => {
    imageRef.current.cache();
    imageRef.current.filters([Konva.Filters.Brighten]);
    imageRef.current.brightness(brightness);
    imageLayerRef.current.batchDraw();
  };

  return (
    <div ref={containerRef} className={classes.container} style={style}>
      <Toolbar
        applyFilters={applyFilters}
        cropToolRef={cropToolRef}
        imageRef={imageRef}
        image={image}
        formImage={formImage}
      />
      <div className={classes.stage}>
        {!image || saving ? (
          <div className={classes.loading}>
            <Typography variant="h5" color="primary">
              {saving ? 'Saving' : 'Loading'} Photo...
            </Typography>
            <CircularProgress size={20} className={classes.circularProgress} />
          </div>
        ) : (
          <Stage ref={stageRef} {...stageProps}>
            <Layer ref={imageLayerRef}>
              <Image
                key={`${imageProps.width} x ${imageProps.height}`}
                ref={imageRef}
                image={image}
                crop={imageCrop}
                rotation={formImage.rotateDegree}
                {...imageProps}
              />
            </Layer>
            {/* 
                CropTool does not access ImageEditorContext directly b/c it is a child component of Stage.
                It is possible to "bridge" the context, but it's just easier to pass necessary props down.
                Related Issue: https://github.com/konvajs/react-konva/issues/188
            */}
            <CropTool
              ref={cropToolRef}
              dispatch={dispatch}
              cropping={cropping}
              cropToolProps={cropToolProps}
              keepAspectRatio={keepAspectRatio}
            />
          </Stage>
        )}
      </div>
    </div>
  );
}

ImageEditor.propTypes = propTypes;
export default withStyles(styles)(ImageEditor);
